// hooks/useRelatedFollowActions.tsx
// 関連フォローの共有ロジック
//
// ホスト側の使い方:
//   const related = useRelatedFollowActions({ anchor, contactUsers });
//   anchor = 選択/編集中フォローの { followNo, groupNo, companyName?, customerName? }（未選択は null）
//   → anchor の followNo が変われば内部状態は自動リセット、groupNo が変われば追従する
//     （旧 initGroupNo/reset の手動呼び出しは不要）
//   ボタン: related.hasRelation で「関連フォロー表示」を出し分け、onClick は openRelatedFollow / openLinkFollow
//   モーダルを閉じたいだけの時: related.closeModals()
//   描画:   {related.renderModals()}
//
//   コメントCRUD: RelatedFollowModal のタイムライン内で選択中の関連フォローに useFollowComments を追従させ、
//   保存/削除結果を relatedFollows の該当要素へ同期する（RelatedFollowModal 自体は表示のみの薄い層）

import React, { useState, useEffect, useRef } from 'react';
import { message } from 'antd';
import RelatedFollowModal, { RelatedFollow } from '../components/RelatedFollowModal';
import LinkFollowModal, { LinkFollowItem } from '../components/LinkFollowModal';
import { followAPI, toFollowComment } from '../services/follow-api';
import { previewAttachFile, downloadAttachFile } from '../utils/fileOperations';
import { useFollowComments } from './useFollowComments';
import { useAuth } from '../contexts/AuthContext';
import { MODAL_Z } from '../utils/modalZIndex';

// ----------------------------------------------------------------
// 表示用変換（純関数）
// ----------------------------------------------------------------

/** 添付サイズ（KB文字列）を表示用に整形 */
const formatAttachSize = (sizeKb: any): string => {
  const kb = parseFloat(sizeKb || '0');
  if (!kb) return '';
  return kb < 1024 ? `${Math.round(kb)} KB` : `${(kb / 1024).toFixed(2)} MB`;
};

/** API のフォロー1件を RelatedFollow（表示用）に変換 */
const toRelatedFollow = (f: any): RelatedFollow => ({
  id: f.followno,
  followNo: String(f.followno ?? ''),
  followDate: f.followdate || '',
  companyName: f.companyname || '',
  customerName: f.customername || '',
  assignee: f.contactusername || '',
  content: f.followcontent || '',
  attachments: (f.attachfile || []).map((a: any) => ({
    id: a.attachfileno,
    name: a.attachfilename || a.filename || '',
    size: formatAttachSize(a.attachfilesize),
  })),
  comments: (f.followcomment || []).map(toFollowComment),
});

/** API のフォロー1件を LinkFollowItem（紐づけ候補用）に変換 */
const toLinkFollowItem = (f: any): LinkFollowItem => ({
  id: f.followno,
  followNo: String(f.followno ?? ''),
  followDate: f.followdate || '',
  companyName: f.companyname || '',
  customerName: f.customername || '',
  assignee: f.contactusername || '',
  assigneeUserno: f.contactuserno ?? 0,
  content: f.followcontent || '',
  isOnsite: !!f.outgovisitflag,
  relationFollowGroupNo: f.relationfollowgroupno ?? null,
});

// ----------------------------------------------------------------
// 型
// ----------------------------------------------------------------

/** 関連フォローのアンカー（紐づけ元フォロー）。ホストが把握している最新値を渡す */
export interface RelatedFollowAnchor {
  /** アンカーのフォローNo */
  followNo: number;
  /** アンカーが所属する関連フォロー群No（未所属は null） */
  groupNo: number | null;
  /** 紐づけ検索の初期シード（会社名） */
  companyName?: string;
  /** 紐づけ検索の初期シード（顧客名） */
  customerName?: string;
}

export interface UseRelatedFollowActionsOptions {
  /** アンカー（紐づけ元）。未選択時は null */
  anchor: RelatedFollowAnchor | null;
  /** 担当者候補一覧（モーダルの検索フィルタ等に使用） */
  contactUsers: { userno: number; username: string; deptname: string }[];
  /** RelatedFollowModal の zIndex */
  relatedZIndex?: number;
  /** LinkFollowModal（単独「フォローの紐づけ」用）の zIndex */
  linkZIndex?: number;
}

export interface UseRelatedFollowActionsReturn {
  /** アンカーが生きた関連フォロー群に所属しているか（「関連フォロー表示」ボタンの表示制御） */
  hasRelation: boolean;
  /** 「関連フォロー表示」を開く（＋一覧取得） */
  openRelatedFollow: () => void;
  /** 「フォローの紐づけ」を開く */
  openLinkFollow: () => void;
  /** 開いているモーダルを閉じるだけの補助（データはリセットしない） */
  closeModals: () => void;
  /** RelatedFollowModal / LinkFollowModal を描画する */
  renderModals: () => React.ReactNode;
}

// ----------------------------------------------------------------
// フック本体
// ----------------------------------------------------------------

export const useRelatedFollowActions = (
  options: UseRelatedFollowActionsOptions,
): UseRelatedFollowActionsReturn => {
  const { anchor, contactUsers, relatedZIndex, linkZIndex } = options;

  const anchorFollowNo = anchor?.followNo ?? null;
  const anchorGroupNo = anchor?.groupNo ?? null;
  const seedCompanyName = anchor?.companyName;
  const seedCustomerName = anchor?.customerName;

  const [relatedFollowVisible, setRelatedFollowVisible] = useState<boolean>(false);
  const [linkFollowVisible, setLinkFollowVisible] = useState<boolean>(false);
  const [relatedFollows, setRelatedFollows] = useState<RelatedFollow[]>([]);
  // アンカーの所属群No。「関連フォローあり」の単一の真実（null＝群なし）
  const [relatedFollowGroupNo, setRelatedFollowGroupNo] = useState<number | null>(anchorGroupNo);
  const [relatedFollowsLoading, setRelatedFollowsLoading] = useState<boolean>(false);

  // 飛行中の一覧取得を無効化するための世代カウンタ（stale 応答の復活防止）
  const loadSeqRef = useRef<number>(0);

  const hasRelation = relatedFollowGroupNo != null;

  // ログインユーザーNo（コメントの本人編集/削除判定用）
  const { user } = useAuth();

  // RelatedFollowModal のタイムライン内で現在選択中の関連フォロー（コメントフックの追従先）
  const [currentCommentFollowNo, setCurrentCommentFollowNo] = useState<number | null>(null);

  // 選択中の関連フォローのコメント一覧を、対象の relatedFollows 要素へ同期する
  const followComments = useFollowComments({
    followno: currentCommentFollowNo,
    contactUsers,
    initialComments: relatedFollows.find(f => f.id === currentCommentFollowNo)?.comments,
    onCommentsChange: (comments) => {
      setRelatedFollows(prev => prev.map(f => (f.id === currentCommentFollowNo ? { ...f, comments } : f)));
    },
    zIndex: (relatedZIndex ?? MODAL_Z.L2) + 10,
  });

  // アンカー切替（followNo 変化）: モーダルを閉じ・一覧クリア・群Noをアンカー値へ・飛行中取得を破棄
  useEffect(() => {
    loadSeqRef.current++;
    setRelatedFollowVisible(false);
    setLinkFollowVisible(false);
    setRelatedFollows([]);
    setRelatedFollowsLoading(false);
    setRelatedFollowGroupNo(anchorGroupNo);
    // anchorGroupNo は followNo 切替時に必ず同時評価されるため依存に含めない（下の effect が追従を担当）
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorFollowNo]);

  // 同一アンカーのままホスト側で群Noが更新されたら追従（編集保存後の詳細再取得など）
  useEffect(() => {
    setRelatedFollowGroupNo(anchorGroupNo);
  }, [anchorGroupNo]);

  /** 対象フォローの関連フォロー一覧を取得して state に反映（群Noも最新化） */
  const loadRelatedFollows = async (followno: number) => {
    const seq = ++loadSeqRef.current;
    setRelatedFollowsLoading(true);
    try {
      const { follows, relationfollowgroupno } = await followAPI.getRelatedFollows(followno);
      if (seq !== loadSeqRef.current) return;
      setRelatedFollows(follows.map(toRelatedFollow));
      setRelatedFollowGroupNo(relationfollowgroupno);
    } catch {
      if (seq !== loadSeqRef.current) return;
      // 群Noは保持する（取得失敗を「群なし」と混同しない。ボタンは残り再試行可能）
      setRelatedFollows([]);
      message.error('関連フォローの取得に失敗しました');
    } finally {
      if (seq === loadSeqRef.current) setRelatedFollowsLoading(false);
    }
  };

  const closeModals = () => {
    setRelatedFollowVisible(false);
    setLinkFollowVisible(false);
  };

  const openRelatedFollow = () => {
    if (anchorFollowNo == null) return;
    setRelatedFollowVisible(true);
    loadRelatedFollows(anchorFollowNo);
  };

  const openLinkFollow = () => {
    if (anchorFollowNo == null) return;
    setLinkFollowVisible(true);
  };

  /** 紐づけ候補フォローの検索（LinkFollowModal から呼ばれる） */
  const handleSearchLinkable = async (
    params: { companyName?: string; customerName?: string; assigneeUserno?: number | null },
  ): Promise<LinkFollowItem[]> => {
    if (anchorFollowNo == null) return [];
    const { follows } = await followAPI.searchLinkableFollows({
      followNo: anchorFollowNo,
      relationFollowGroupNo: relatedFollowGroupNo,
      searchCompanyName: params.companyName || undefined,
      searchCustomerName: params.customerName || undefined,
      searchContactUserNoCsv: params.assigneeUserno != null ? String(params.assigneeUserno) : undefined,
      viewCount: 50,
      listPage: 1,
    });
    return follows.map(toLinkFollowItem);
  };

  /** 紐づけ実行（成功後は関連一覧を再取得して関連モーダルを表示） */
  const handleAddFollowLink = async (payload: { followNos: number[]; groupNos: number[] }) => {
    if (anchorFollowNo == null) return;
    if (payload.followNos.length + payload.groupNos.length === 0) return;
    try {
      await followAPI.addFollowLink(anchorFollowNo, payload.followNos, payload.groupNos, relatedFollowGroupNo);
      message.success('フォローを紐づけました');
      setRelatedFollowVisible(true);
      await loadRelatedFollows(anchorFollowNo);
    } catch {
      message.error('フォローの紐づけに失敗しました');
    }
  };

  /** 他フォローの個別切り離し（Web版 btnPickout 相当：再取得してモーダルは開いたまま） */
  const handleDetachFollow = async (follow: RelatedFollow) => {
    if (anchorFollowNo == null) return;
    try {
      const newGroupNo = await followAPI.cutFollow(follow.id, relatedFollowGroupNo);
      message.success('関連フォローを切り離しました');
      // 切り離しの結果、群が解散（残り1件で群が消滅し API が null を返す）した場合は
      // 「切り離して閉じる」と同様にダイアログを閉じる（再取得はしない）
      if (newGroupNo == null) {
        // 飛行中の loadRelatedFollows があれば世代を進めて無効化し、
        // 後から届く応答がこの「閉じる」判定を上書きしないようにする
        loadSeqRef.current++;
        setRelatedFollowVisible(false);
        setRelatedFollowGroupNo(null);
        return;
      }
      await loadRelatedFollows(anchorFollowNo);
    } catch {
      message.error('切り離しに失敗しました');
    }
  };

  /** 自分（アンカー）の切り離し（Web版 btnCutself 相当：cutFollow 後にモーダルを閉じる） */
  const handleDetachSelf = async (follow: RelatedFollow) => {
    if (anchorFollowNo == null) return;
    try {
      await followAPI.cutFollow(follow.id, relatedFollowGroupNo);
      message.success('関連フォローを切り離しました');
      setRelatedFollowVisible(false);
      setRelatedFollowGroupNo(null);
    } catch {
      message.error('切り離しに失敗しました');
    }
  };

  /** 全件切り離し（Web版 btnCut 相当：cutFollowAll 後にモーダルを閉じる） */
  const handleDetachAll = async () => {
    if (anchorFollowNo == null) return;
    try {
      await followAPI.cutFollowAll(anchorFollowNo, relatedFollowGroupNo);
      message.success('関連フォローをすべて切り離しました');
      setRelatedFollowVisible(false);
      setRelatedFollowGroupNo(null);
    } catch {
      message.error('切り離しに失敗しました');
    }
  };

  /** 関連フォローの添付プレビュー */
  const handlePreviewAttach = (attachment: { id: number; name: string }) => {
    previewAttachFile({ attachfileno: Number(attachment.id), filename: attachment.name });
  };

  /** 関連フォローの添付ダウンロード */
  const handleDownloadAttach = (attachment: { id: number; name: string }) => {
    downloadAttachFile({ attachfileno: Number(attachment.id), filename: attachment.name });
  };

  // ----------------------------------------------------------------
  // モーダルレンダリング
  // ----------------------------------------------------------------
  const renderModals = (): React.ReactNode => (
    <>
      {/* フォロー紐づけモーダル（単独「フォローの紐づけ」ボタン用） */}
      <LinkFollowModal
        open={linkFollowVisible}
        onCancel={() => setLinkFollowVisible(false)}
        onLink={(payload) => {
          setLinkFollowVisible(false);
          handleAddFollowLink(payload);
        }}
        onSearch={handleSearchLinkable}
        contactUsers={contactUsers}
        initialCompanyName={seedCompanyName}
        initialCustomerName={seedCustomerName}
        zIndex={linkZIndex}
      />

      {/* 関連フォローモーダル */}
      <RelatedFollowModal
        open={relatedFollowVisible}
        onCancel={() => setRelatedFollowVisible(false)}
        follows={relatedFollows}
        loading={relatedFollowsLoading}
        anchorFollowNo={anchorFollowNo}
        contactUsers={contactUsers}
        onDetachFollow={handleDetachFollow}
        onDetachSelf={handleDetachSelf}
        onDetachAll={handleDetachAll}
        onAddLink={handleAddFollowLink}
        onSearchLinkable={handleSearchLinkable}
        onPreviewAttachment={handlePreviewAttach}
        onDownloadAttachment={handleDownloadAttach}
        onCurrentFollowChange={setCurrentCommentFollowNo}
        onAddComment={followComments.openAdd}
        onEditComment={followComments.openEdit}
        onDeleteComment={followComments.requestDelete}
        currentUserNo={user?.userno ?? null}
        initialCompanyName={seedCompanyName}
        initialCustomerName={seedCustomerName}
        zIndex={relatedZIndex}
      />

      {/* コメント入力・通知先選択・削除確認モーダル */}
      {followComments.renderModals()}
    </>
  );

  return {
    hasRelation,
    openRelatedFollow,
    openLinkFollow,
    closeModals,
    renderModals,
  };
};
